import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Users, Briefcase, Mic, MicOff, Square, Loader2, Volume2, VolumeX, TrendingUp, DollarSign, Trophy, ChevronRight, Lightbulb, Target, Sparkles, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';
import { Helmet } from 'react-helmet-async';

type GamePhase = 'landing' | 'setup' | 'choose_mode' | 'briefing' | 'pitch' | 'questions' | 'answering' | 'deliberation' | 'verdict' | 'leaderboard';

interface Player {
  id: string;
  name: string;
}

interface StartupBrief {
  startupName: string;
  sector: string;
  problem: string;
  concept: string;
  targetMarket: string;
  funFact: string;
}

interface InvestorQuestion {
  question: string;
  investorName: string;
  investorStyle?: string;
}

interface ResponseAnalysis {
  score: number;
  feedback: string;
  investorReaction: 'impressed' | 'neutral' | 'skeptical';
}

interface PitchResult {
  playerName: string;
  scores: {
    clarity: number;
    viability: number;
    charisma: number;
    responseQuality: number;
    innovation: number;
  };
  scoreTotal: number;
  verdict: 'invest' | 'counter_offer' | 'pass';
  investmentAmount: number;
  equityPercentage: number;
  feedback: string;
  strengths: string[];
  weaknesses: string[];
  investorQuote: string;
  brief: StartupBrief | null;
}

interface QAEntry {
  question: string;
  answer: string;
}

// Fallback briefs
const fallbackBriefs: StartupBrief[] = [
  { startupName: 'SleepBot', sector: 'HealthTech', problem: 'Les gens dorment mal à cause des écrans', concept: 'Un oreiller connecté qui analyse le sommeil et diffuse des sons apaisants', targetMarket: 'Adultes 25-45 ans urbains', funFact: '1 Français sur 3 souffre de troubles du sommeil' },
  { startupName: 'PetLingo', sector: 'PetTech', problem: 'Les propriétaires ne comprennent pas leur animal', concept: 'Un collier IA qui traduit les émotions de votre animal en notifications', targetMarket: 'Propriétaires d\'animaux millennials', funFact: 'Le marché du pet-tech pèse 8 milliards d\'euros' },
  { startupName: 'GreenBite', sector: 'FoodTech', problem: 'Le gaspillage alimentaire des restaurants', concept: 'Une app qui connecte les restaurants aux associations en temps réel pour redistribuer les invendus', targetMarket: 'Restaurants et associations caritatives', funFact: '10 millions de tonnes de nourriture gaspillées par an en France' },
];

const fallbackQuestions: InvestorQuestion[] = [
  { question: 'Quel est votre modèle économique et comment comptez-vous générer des revenus dans les 12 premiers mois ?', investorName: 'Marc' },
  { question: 'Quels sont vos principaux concurrents et qu\'est-ce qui vous différencie vraiment d\'eux ?', investorName: 'Sophie' },
];

export default function StartupPitch() {
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [useOwnIdea, setUseOwnIdea] = useState(false);
  const [currentBrief, setCurrentBrief] = useState<StartupBrief | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Pitch recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [pitchText, setPitchText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  
  // Q&A
  const [currentQuestion, setCurrentQuestion] = useState<InvestorQuestion | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isGeneratingQuestion, setIsGeneratingQuestion] = useState(false);
  const [qaEntries, setQaEntries] = useState<QAEntry[]>([]);
  const [currentAnswerText, setCurrentAnswerText] = useState('');
  const [isRecordingAnswer, setIsRecordingAnswer] = useState(false);
  const [answerRecordingTime, setAnswerRecordingTime] = useState(0);
  const [isTranscribingAnswer, setIsTranscribingAnswer] = useState(false);
  const [currentResponseAnalysis, setCurrentResponseAnalysis] = useState<ResponseAnalysis | null>(null);
  const [isAnalyzingResponse, setIsAnalyzingResponse] = useState(false);
  
  // TTS
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  
  // Verdict
  const [isAnalyzingPitch, setIsAnalyzingPitch] = useState(false);
  const [currentVerdict, setCurrentVerdict] = useState<PitchResult | null>(null);
  
  // All results
  const [results, setResults] = useState<PitchResult[]>([]);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const { toast } = useToast();
  const { t, language } = useLanguage();

  const currentPlayer = players[currentPlayerIndex];
  const MAX_QUESTIONS = 2;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const addPlayer = () => {
    if (playerName.trim() && players.length < 6) {
      setPlayers([...players, { id: crypto.randomUUID(), name: playerName.trim() }]);
      setPlayerName('');
    }
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  // TTS
  const speak = useCallback(async (text: string) => {
    if (!voiceEnabled) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
    
    setIsSpeaking(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ text, voiceType: 'pirate' }),
        }
      );
      if (!response.ok) throw new Error('TTS failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  }, [voiceEnabled]);

  // Recording helpers
  const startRecordingGeneric = useCallback(async (
    setRec: (v: boolean) => void,
    setTime: (v: number | ((p: number) => number)) => void
  ) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.start(1000);
      setRec(true);
      setTime(0);
      timerRef.current = setInterval(() => setTime(prev => (prev as number) + 1), 1000);
    } catch {
      toast({ title: t('startup.mic_error'), description: t('startup.mic_error_desc'), variant: "destructive" });
    }
  }, [toast, t]);

  const stopRecordingGeneric = useCallback(async (setRec: (v: boolean) => void): Promise<Blob> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) { resolve(new Blob()); return; }
      mediaRecorderRef.current.onstop = () => resolve(new Blob(audioChunksRef.current, { type: 'audio/webm' }));
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRec(false);
    });
  }, []);

  const transcribeAudio = async (blob: Blob): Promise<string> => {
    const formData = new FormData();
    formData.append('audio', blob, 'recording.webm');
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-defense`,
      { method: 'POST', headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` }, body: formData }
    );
    if (!response.ok) throw new Error('Transcription failed');
    const data = await response.json();
    if (!data.text) throw new Error('No transcription');
    return data.text;
  };

  // Generate brief
  const generateBrief = async () => {
    if (!currentPlayer) return;
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-startup-brief', {
        body: { playerName: currentPlayer.name, language }
      });
      if (error) throw error;
      if (data?.startupName) { setCurrentBrief(data); }
      else throw new Error('Invalid response');
    } catch {
      const fallback = fallbackBriefs[Math.floor(Math.random() * fallbackBriefs.length)];
      setCurrentBrief(fallback);
      toast({ title: "Mode hors-ligne", description: "Brief classique utilisé" });
    } finally {
      setIsGenerating(false);
    }
  };

  // Start briefing phase
  const startBriefing = async (ownIdea: boolean) => {
    setUseOwnIdea(ownIdea);
    if (ownIdea) {
      setCurrentBrief(null);
      setPhase('briefing');
    } else {
      await generateBrief();
      setPhase('briefing');
    }
  };

  // Pitch recording
  const startPitchRecording = () => startRecordingGeneric(setIsRecording, setRecordingTime);
  
  const stopPitchRecording = async () => {
    setIsTranscribing(true);
    try {
      const blob = await stopRecordingGeneric(setIsRecording);
      if (!blob.size) throw new Error('No audio');
      const text = await transcribeAudio(blob);
      setPitchText(text);
    } catch {
      toast({ title: t('startup.transcription_error'), variant: "destructive" });
    } finally {
      setIsTranscribing(false);
    }
  };

  // Generate investor question
  const generateQuestion = async () => {
    if (!pitchText) return;
    setIsGeneratingQuestion(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-investor-question', {
        body: {
          pitchText, brief: currentBrief, playerName: currentPlayer?.name,
          questionNumber: questionIndex + 1,
          previousQuestions: qaEntries.map(q => q.question),
          language,
        }
      });
      if (error) throw error;
      if (data?.question) {
        setCurrentQuestion(data);
        if (voiceEnabled) speak(data.question);
      } else throw new Error('Invalid');
    } catch {
      const fb = fallbackQuestions[questionIndex % fallbackQuestions.length];
      setCurrentQuestion(fb);
      if (voiceEnabled) speak(fb.question);
    } finally {
      setIsGeneratingQuestion(false);
    }
  };

  // Answer recording
  const startAnswerRecording = () => {
    setCurrentAnswerText('');
    setCurrentResponseAnalysis(null);
    startRecordingGeneric(setIsRecordingAnswer, setAnswerRecordingTime);
  };

  const stopAnswerRecording = async () => {
    setIsTranscribingAnswer(true);
    try {
      const blob = await stopRecordingGeneric(setIsRecordingAnswer);
      if (!blob.size) throw new Error('No audio');
      const text = await transcribeAudio(blob);
      setCurrentAnswerText(text);
      // Analyze response
      setIsAnalyzingResponse(true);
      try {
        const { data, error } = await supabase.functions.invoke('analyze-pitch-response', {
          body: { responseText: text, question: currentQuestion?.question, pitchText, brief: currentBrief, playerName: currentPlayer?.name, language }
        });
        if (error) throw error;
        if (data?.score !== undefined) setCurrentResponseAnalysis(data);
      } catch { /* silent */ }
      finally { setIsAnalyzingResponse(false); }
    } catch {
      toast({ title: t('startup.transcription_error'), variant: "destructive" });
    } finally {
      setIsTranscribingAnswer(false);
    }
  };

  // Next question or go to deliberation
  const nextQuestion = () => {
    const entry: QAEntry = { question: currentQuestion?.question || '', answer: currentAnswerText };
    const newEntries = [...qaEntries, entry];
    setQaEntries(newEntries);
    setCurrentQuestion(null);
    setCurrentAnswerText('');
    setCurrentResponseAnalysis(null);
    
    if (questionIndex + 1 >= MAX_QUESTIONS) {
      // Go to deliberation
      setPhase('deliberation');
      analyzePitch(newEntries);
    } else {
      setQuestionIndex(questionIndex + 1);
      setPhase('questions');
    }
  };

  // Analyze full pitch
  const analyzePitch = async (allQA: QAEntry[]) => {
    setIsAnalyzingPitch(true);
    try {
      const responses = allQA.map(qa => ({ question: qa.question, answer: qa.answer }));
      const { data, error } = await supabase.functions.invoke('analyze-pitch', {
        body: { pitchText, brief: currentBrief, playerName: currentPlayer?.name, responses, language }
      });
      if (error) throw error;
      if (data?.scoreTotal !== undefined) {
        const result: PitchResult = { ...data, playerName: currentPlayer?.name || '', brief: currentBrief };
        setCurrentVerdict(result);
        setPhase('verdict');
        // Speak the investor quote
        if (voiceEnabled && data.investorQuote) speak(data.investorQuote);
      } else throw new Error('Invalid');
    } catch {
      // Fallback verdict
      const fallbackResult: PitchResult = {
        playerName: currentPlayer?.name || '',
        scores: { clarity: 12, viability: 10, charisma: 14, responseQuality: 11, innovation: 13 },
        scoreTotal: 60,
        verdict: 'counter_offer',
        investmentAmount: 50000,
        equityPercentage: 20,
        feedback: t('startup.fallback_feedback'),
        strengths: [t('startup.fallback_strength')],
        weaknesses: [t('startup.fallback_weakness')],
        investorQuote: t('startup.fallback_quote'),
        brief: currentBrief,
      };
      setCurrentVerdict(fallbackResult);
      setPhase('verdict');
    } finally {
      setIsAnalyzingPitch(false);
    }
  };

  // Next player or go to leaderboard
  const nextPlayer = () => {
    if (currentVerdict) {
      setResults([...results, currentVerdict]);
    }
    if (currentPlayerIndex + 1 < players.length) {
      setCurrentPlayerIndex(currentPlayerIndex + 1);
      setCurrentBrief(null);
      setPitchText('');
      setQaEntries([]);
      setQuestionIndex(0);
      setCurrentQuestion(null);
      setCurrentVerdict(null);
      setCurrentAnswerText('');
      setCurrentResponseAnalysis(null);
      setPhase('choose_mode');
    } else {
      const allResults = currentVerdict ? [...results, currentVerdict] : results;
      setResults(allResults);
      setPhase('leaderboard');
    }
  };

  const resetGame = () => {
    setPhase('landing');
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setResults([]);
    setCurrentBrief(null);
    setPitchText('');
    setQaEntries([]);
    setQuestionIndex(0);
    setCurrentQuestion(null);
    setCurrentVerdict(null);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const verdictColor = (v: string) => {
    if (v === 'invest') return 'text-emerald-400';
    if (v === 'counter_offer') return 'text-amber-400';
    return 'text-red-400';
  };

  const verdictEmoji = (v: string) => {
    if (v === 'invest') return '🤝';
    if (v === 'counter_offer') return '🤔';
    return '❌';
  };

  const verdictLabel = (v: string) => {
    if (v === 'invest') return t('startup.verdict_invest');
    if (v === 'counter_offer') return t('startup.verdict_counter');
    return t('startup.verdict_pass');
  };

  return (
    <>
      <Helmet>
        <title>{t('game.startup-pitch.name')} - SuperSociety.ai</title>
        <meta name="description" content={t('game.startup-pitch.description')} />
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full opacity-30"
            style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.2) 0%, hsl(45 93% 47% / 0.1) 40%, transparent 70%)' }} />
          <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full opacity-20 animate-pulse"
            style={{ background: 'radial-gradient(circle, hsl(45 93% 47% / 0.3) 0%, transparent 60%)', animationDuration: '6s' }} />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-6 max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('nav.back')}</span>
            </Link>
            <button onClick={() => setVoiceEnabled(!voiceEnabled)} className="p-2 rounded-full bg-card/50 border border-border/30 text-muted-foreground hover:text-foreground transition-colors">
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>

          {/* LANDING */}
          {phase === 'landing' && (
            <div className="text-center space-y-8 py-12 slide-up">
              <div className="text-8xl mb-4">💼</div>
              <h1 className="font-display text-4xl md:text-5xl" style={{ color: 'hsl(160 84% 39%)' }}>
                {t('game.startup-pitch.name')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">{t('game.startup-pitch.description')}</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-border/30">
                  <Users className="w-4 h-4" /> {t('game.startup-pitch.players')}
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-border/30">
                  <Mic className="w-4 h-4" /> {t('startup.voice_pitch')}
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-border/30">
                  <TrendingUp className="w-4 h-4" /> {t('startup.ai_investors')}
                </span>
              </div>
              <Button onClick={() => setPhase('setup')} size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-2xl shadow-lg shadow-emerald-600/30">
                <Play className="w-5 h-5 mr-2" /> {t('game.play')}
              </Button>
            </div>
          )}

          {/* SETUP - Player names + choose mode */}
          {phase === 'setup' && currentPlayerIndex === 0 && players.length === 0 && (
            <div className="space-y-6 slide-up">
              <h2 className="font-display text-2xl text-center" style={{ color: 'hsl(160 84% 39%)' }}>{t('startup.add_players')}</h2>
              <div className="flex gap-2">
                <Input value={playerName} onChange={(e) => setPlayerName(e.target.value)} placeholder={t('startup.player_name')}
                  onKeyDown={(e) => e.key === 'Enter' && addPlayer()} className="bg-card/50 border-border/30" />
                <Button onClick={addPlayer} disabled={!playerName.trim() || players.length >= 6} variant="outline">+</Button>
              </div>
              {players.length > 0 && (
                <div className="space-y-2">
                  {players.map((p, i) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-2 rounded-xl bg-card/50 border border-border/30">
                      <span className="text-foreground">{i + 1}. {p.name}</span>
                      <button onClick={() => removePlayer(p.id)} className="text-muted-foreground hover:text-red-400 text-sm">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {players.length >= 2 && (
                <Button onClick={() => setPhase('choose_mode')} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                  {t('startup.start_game')} ({players.length} {t('game.players')})
                </Button>
              )}
            </div>
          )}

          {/* Choose: own idea or generated brief */}
          {phase === 'choose_mode' && (
            <div className="space-y-6 slide-up text-center">
              <div className="text-6xl mb-2">💼</div>
              <h2 className="font-display text-2xl" style={{ color: 'hsl(160 84% 39%)' }}>
                {t('startup.turn_of')} {currentPlayer?.name}
              </h2>
              <p className="text-muted-foreground">{t('startup.choose_mode')}</p>
              <div className="grid gap-4">
                <Button onClick={() => startBriefing(false)} size="lg" variant="outline"
                  className="flex items-center gap-3 py-6 rounded-xl border-emerald-500/30 hover:bg-emerald-500/10">
                  <Lightbulb className="w-5 h-5 text-amber-400" />
                  <div className="text-left">
                    <div className="font-medium">{t('startup.generated_idea')}</div>
                    <div className="text-sm text-muted-foreground">{t('startup.generated_idea_desc')}</div>
                  </div>
                </Button>
                <Button onClick={() => startBriefing(true)} size="lg" variant="outline"
                  className="flex items-center gap-3 py-6 rounded-xl border-amber-500/30 hover:bg-amber-500/10">
                  <Rocket className="w-5 h-5 text-emerald-400" />
                  <div className="text-left">
                    <div className="font-medium">{t('startup.own_idea')}</div>
                    <div className="text-sm text-muted-foreground">{t('startup.own_idea_desc')}</div>
                  </div>
                </Button>
              </div>
            </div>
          )}

          {/* BRIEFING */}
          {phase === 'briefing' && (
            <div className="space-y-6 slide-up">
              {isGenerating ? (
                <div className="text-center py-12 space-y-4">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto text-emerald-400" />
                  <p className="text-muted-foreground">{t('startup.generating_brief')}</p>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-2xl text-center" style={{ color: 'hsl(160 84% 39%)' }}>
                    {currentPlayer?.name}, {useOwnIdea ? t('startup.pitch_your_idea') : t('startup.your_startup')}
                  </h2>
                  {currentBrief && !useOwnIdea && (
                    <div className="p-6 rounded-2xl bg-card/60 border border-emerald-500/30 space-y-4">
                      <div className="flex items-center gap-3">
                        <Rocket className="w-6 h-6 text-emerald-400" />
                        <h3 className="font-display text-xl text-emerald-400">{currentBrief.startupName}</h3>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs">{currentBrief.sector}</span>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div><span className="text-muted-foreground font-medium">{t('startup.problem')}:</span> <span className="text-foreground">{currentBrief.problem}</span></div>
                        <div><span className="text-muted-foreground font-medium">{t('startup.concept')}:</span> <span className="text-foreground">{currentBrief.concept}</span></div>
                        <div><span className="text-muted-foreground font-medium">{t('startup.market')}:</span> <span className="text-foreground">{currentBrief.targetMarket}</span></div>
                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-start gap-2">
                          <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                          {currentBrief.funFact}
                        </div>
                      </div>
                    </div>
                  )}
                  {useOwnIdea && (
                    <div className="p-6 rounded-2xl bg-card/60 border border-amber-500/30 text-center space-y-2">
                      <Rocket className="w-8 h-8 mx-auto text-amber-400" />
                      <p className="text-muted-foreground">{t('startup.own_idea_instructions')}</p>
                    </div>
                  )}
                  <Button onClick={() => setPhase('pitch')} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                    {t('startup.start_pitch')} <Mic className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          )}

          {/* PITCH RECORDING */}
          {phase === 'pitch' && (
            <div className="space-y-6 slide-up text-center">
              <h2 className="font-display text-2xl" style={{ color: 'hsl(160 84% 39%)' }}>
                {currentPlayer?.name}, {t('startup.pitch_now')}!
              </h2>
              <p className="text-sm text-muted-foreground">{t('startup.pitch_instructions')}</p>
              
              {!pitchText && (
                <div className="space-y-4">
                  {isRecording && (
                    <div className="space-y-3">
                      <div className="text-4xl font-mono text-emerald-400">{formatTime(recordingTime)}</div>
                      <div className="w-full h-2 rounded-full bg-card/50 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-500 to-amber-500 rounded-full transition-all" style={{ width: `${Math.min((recordingTime / 90) * 100, 100)}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground">{t('startup.max_90s')}</p>
                    </div>
                  )}
                  
                  {!isRecording && !isTranscribing && (
                    <Button onClick={startPitchRecording} size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-full w-20 h-20 mx-auto">
                      <Mic className="w-8 h-8" />
                    </Button>
                  )}
                  
                  {isRecording && (
                    <Button onClick={stopPitchRecording} size="lg" variant="outline" className="rounded-full w-20 h-20 mx-auto border-red-500/50">
                      <Square className="w-6 h-6 text-red-400" />
                    </Button>
                  )}
                  
                  {isTranscribing && (
                    <div className="space-y-2">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-400" />
                      <p className="text-sm text-muted-foreground">{t('startup.transcribing')}</p>
                    </div>
                  )}
                </div>
              )}
              
              {pitchText && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-card/50 border border-border/30 text-left text-sm text-foreground max-h-40 overflow-y-auto">
                    "{pitchText}"
                  </div>
                  <Button onClick={() => { setPhase('questions'); generateQuestion(); }} size="lg"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                    {t('startup.face_investors')} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button onClick={() => setPitchText('')} variant="ghost" size="sm" className="text-muted-foreground">
                    {t('startup.redo_pitch')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* INVESTOR QUESTIONS */}
          {phase === 'questions' && (
            <div className="space-y-6 slide-up">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl" style={{ color: 'hsl(45 93% 47%)' }}>
                  {t('startup.investor_questions')}
                </h2>
                <span className="text-sm text-muted-foreground">{questionIndex + 1}/{MAX_QUESTIONS}</span>
              </div>
              
              {isGeneratingQuestion ? (
                <div className="text-center py-8 space-y-3">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-400" />
                  <p className="text-sm text-muted-foreground">{t('startup.investor_thinking')}</p>
                </div>
              ) : currentQuestion ? (
                <div className="space-y-4">
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-card/60 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-medium text-amber-400">{currentQuestion.investorName}</span>
                      {isSpeaking && <Volume2 className="w-3 h-3 text-amber-400 animate-pulse" />}
                    </div>
                    <p className="text-foreground font-medium">"{currentQuestion.question}"</p>
                  </div>
                  <Button onClick={() => setPhase('answering')} size="lg" className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl">
                    <Mic className="w-4 h-4 mr-2" /> {t('startup.answer')}
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {/* ANSWERING */}
          {phase === 'answering' && (
            <div className="space-y-6 slide-up text-center">
              <h2 className="font-display text-xl" style={{ color: 'hsl(45 93% 47%)' }}>
                {t('startup.your_answer')}
              </h2>
              <div className="p-3 rounded-xl bg-card/50 border border-border/30 text-sm text-muted-foreground">
                "{currentQuestion?.question}"
              </div>
              
              {!currentAnswerText && (
                <div className="space-y-4">
                  {isRecordingAnswer && (
                    <div className="text-3xl font-mono text-amber-400">{formatTime(answerRecordingTime)}</div>
                  )}
                  {!isRecordingAnswer && !isTranscribingAnswer && (
                    <Button onClick={startAnswerRecording} size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-full w-16 h-16 mx-auto">
                      <Mic className="w-6 h-6" />
                    </Button>
                  )}
                  {isRecordingAnswer && (
                    <Button onClick={stopAnswerRecording} size="lg" variant="outline" className="rounded-full w-16 h-16 mx-auto border-red-500/50">
                      <Square className="w-5 h-5 text-red-400" />
                    </Button>
                  )}
                  {isTranscribingAnswer && (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-400" />
                      <p className="text-sm text-muted-foreground">{t('startup.transcribing')}</p>
                    </div>
                  )}
                </div>
              )}
              
              {currentAnswerText && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-card/50 border border-border/30 text-left text-sm text-foreground max-h-32 overflow-y-auto">
                    "{currentAnswerText}"
                  </div>
                  {isAnalyzingResponse && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> {t('startup.analyzing')}
                    </div>
                  )}
                  {currentResponseAnalysis && (
                    <div className={`p-3 rounded-xl border text-sm ${
                      currentResponseAnalysis.investorReaction === 'impressed' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' :
                      currentResponseAnalysis.investorReaction === 'skeptical' ? 'bg-red-500/10 border-red-500/30 text-red-300' :
                      'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    }`}>
                      {currentResponseAnalysis.feedback}
                    </div>
                  )}
                  <Button onClick={nextQuestion} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                    {questionIndex + 1 >= MAX_QUESTIONS ? t('startup.to_deliberation') : t('startup.next_question')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* DELIBERATION */}
          {phase === 'deliberation' && (
            <div className="text-center py-16 space-y-6 slide-up">
              <div className="text-6xl animate-pulse">🤔</div>
              <h2 className="font-display text-2xl" style={{ color: 'hsl(45 93% 47%)' }}>
                {t('startup.deliberation')}
              </h2>
              <p className="text-muted-foreground">{t('startup.investors_deliberating')}</p>
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-400" />
            </div>
          )}

          {/* VERDICT */}
          {phase === 'verdict' && currentVerdict && (
            <div className="space-y-6 slide-up">
              <div className="text-center space-y-3">
                <div className="text-6xl">{verdictEmoji(currentVerdict.verdict)}</div>
                <h2 className={`font-display text-3xl ${verdictColor(currentVerdict.verdict)}`}>
                  {verdictLabel(currentVerdict.verdict)}
                </h2>
                {currentVerdict.verdict !== 'pass' && (
                  <div className="flex items-center justify-center gap-4 text-lg">
                    <span className="text-emerald-400 font-bold">{currentVerdict.investmentAmount.toLocaleString()}€</span>
                    <span className="text-muted-foreground">{t('startup.for')}</span>
                    <span className="text-amber-400 font-bold">{currentVerdict.equityPercentage}%</span>
                  </div>
                )}
              </div>

              <div className="p-4 rounded-xl bg-card/60 border border-border/30 text-sm italic text-muted-foreground text-center">
                "{currentVerdict.investorQuote}"
              </div>

              {/* Scores */}
              <div className="space-y-3 p-4 rounded-xl bg-card/50 border border-border/30">
                <h3 className="font-medium text-foreground text-sm">{t('startup.scores')}</h3>
                {Object.entries(currentVerdict.scores).map(([key, value]) => (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground capitalize">{t(`startup.score_${key}`)}</span>
                      <span className="text-foreground">{value}/20</span>
                    </div>
                    <Progress value={(value / 20) * 100} className="h-1.5" />
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-border/30">
                  <span className="font-medium text-foreground text-sm">{t('startup.total')}</span>
                  <span className="font-bold text-lg" style={{ color: 'hsl(160 84% 39%)' }}>{currentVerdict.scoreTotal}/100</span>
                </div>
              </div>

              {/* Feedback */}
              <div className="p-4 rounded-xl bg-card/50 border border-border/30 text-sm text-foreground space-y-3">
                <p>{currentVerdict.feedback}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-emerald-400 text-xs font-medium">✓ {t('startup.strengths')}</span>
                    {currentVerdict.strengths.map((s, i) => <p key={i} className="text-xs text-muted-foreground">• {s}</p>)}
                  </div>
                  <div className="space-y-1">
                    <span className="text-red-400 text-xs font-medium">✗ {t('startup.weaknesses')}</span>
                    {currentVerdict.weaknesses.map((w, i) => <p key={i} className="text-xs text-muted-foreground">• {w}</p>)}
                  </div>
                </div>
              </div>

              <Button onClick={nextPlayer} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                {currentPlayerIndex + 1 < players.length
                  ? `${t('startup.next_pitcher')}: ${players[currentPlayerIndex + 1]?.name}`
                  : t('startup.see_results')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* LEADERBOARD */}
          {phase === 'leaderboard' && (
            <div className="space-y-6 slide-up">
              <div className="text-center space-y-3">
                <div className="text-6xl">🏆</div>
                <h2 className="font-display text-3xl" style={{ color: 'hsl(45 93% 47%)' }}>
                  {t('startup.final_ranking')}
                </h2>
              </div>
              
              {results.sort((a, b) => b.scoreTotal - a.scoreTotal).map((r, i) => (
                <div key={i} className={`p-4 rounded-xl border ${i === 0 ? 'bg-gradient-to-r from-amber-500/15 to-emerald-500/15 border-amber-500/40' : 'bg-card/50 border-border/30'}`}>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-muted-foreground">#{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{r.playerName}</span>
                        <span className="text-lg">{verdictEmoji(r.verdict)}</span>
                      </div>
                      {r.brief && <span className="text-xs text-muted-foreground">{r.brief.startupName}</span>}
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold" style={{ color: 'hsl(160 84% 39%)' }}>{r.scoreTotal}/100</div>
                      {r.verdict !== 'pass' && (
                        <div className="text-xs text-amber-400">{r.investmentAmount.toLocaleString()}€</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <Button onClick={resetGame} size="lg" variant="outline" className="w-full rounded-xl">
                {t('startup.play_again')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
